import type { Block, FoundDecision, Section, SectionPlanItem, Source } from "../contract"
import { isAnatomy, slotsFor, type Slot } from "./anatomy"
import { BlockView } from "./blocks/Blocks"
import { BlockScopeContext } from "./blocks/context"
import { Fold } from "./Fold"
import { citeNumbers, idx } from "./format"
import { AllDecisions, DepartmentCard, RemindMe } from "./pro"
import { useAnswerSession } from "./session"

const MAX_QUOTES = 2

export const sectionId = (turn: string, i: number) => `sec-${turn}-${i}`

interface AnswerSectionsProps {
  turn: string
  plan: SectionPlanItem[]
  sections: Record<string, Section>
  takeaways: string[]
  sources: Record<string, Source>
  running: boolean
  limits?: string[]
  /** One at a time: only this slot shows, with Previous and Next. */
  only?: string | null
  onGo?: (id: string) => void
  /** The offers on a finished answer; absent on a shared copy. */
  offers?: Offers
}

/** What a finished answer offers beyond its sections (Flow lanes A, D, E). */
export interface Offers {
  found: FoundDecision[]
  /** The matter's name, for the reminder. */
  title: string
  /** Shows where the department's side lives (Notice analysis, Pro); off when the answer already reads for the department. */
  department: boolean
}

/**
 * The answer's sections. A plan in the fixed anatomy reads as the six numbered
 * sections (answer-anatomy.md): each always present, a missing one saying so.
 * Older answers keep their own planned headings.
 */
export function AnswerSections(props: AnswerSectionsProps) {
  const { turn, plan, sections, takeaways, sources, running, limits = [], only, onGo, offers } = props
  if (!isAnatomy(plan)) return <LegacySections {...props} />
  const slots = slotsFor(turn, plan, sections, limits)
  const firstLanded = running ? slots.find((s) => s.section) : undefined
  const shown = only ? slots.filter((s) => s.id === only) : slots
  return (
    <div className="asecs">
      {shown.map((slot) => {
        const at = slots.indexOf(slot)
        const nav = only && onGo ? { prev: slots[at - 1], next: slots[at + 1], onGo } : undefined
        return (
          <SlotView
            key={slot.tag}
            turn={turn}
            slot={slot}
            takeaway={slot.index !== undefined ? takeaways[slot.index] : ""}
            sources={sources}
            running={running}
            first={slot === firstLanded}
            nav={nav}
            offers={running ? undefined : offers}
          />
        )
      })}
    </div>
  )
}

function SlotView({ turn, slot, takeaway, sources, running, first, nav, offers }: {
  turn: string
  slot: Slot
  takeaway: string
  sources: Record<string, Source>
  running: boolean
  first: boolean
  nav?: { prev?: Slot; next?: Slot; onGo: (id: string) => void }
  offers?: Offers
}) {
  const head = (
    <>
      <p className="aeyebrow">{slot.num} · {slot.short}</p>
      <h3 id={`${slot.id}-h`}>{slot.title}</h3>
    </>
  )
  const pager = nav ? (
    <div className="apager">
      {nav.prev ? <button type="button" onClick={() => nav.onGo(nav.prev!.id)}>← {nav.prev.num} {nav.prev.short}</button> : <span />}
      {nav.next ? <button type="button" onClick={() => nav.onGo(nav.next!.id)}>Next · {nav.next.num} {nav.next.short} →</button> : <span />}
    </div>
  ) : null

  if (slot.tag === "LIMITS") {
    if (running && !slot.limits?.length) return <Pending id={slot.id} head={head} />
    return (
      <section className="asec" id={slot.id} aria-labelledby={`${slot.id}-h`}>
        {head}
        {slot.limits?.length ? (
          <ul className="alim">{slot.limits.map((t, i) => <li key={i}>{t}</li>)}</ul>
        ) : (
          <p className="pr">Nira read only what is cited above.</p>
        )}
        <p className="alim-foot">Research, not legal advice. Check the cited text before you rely on it.</p>
        {pager}
      </section>
    )
  }
  if (!slot.section) {
    if (running) return <Pending id={slot.id} head={head} />
    return (
      <section className="asec empty" id={slot.id} aria-labelledby={`${slot.id}-h`}>
        {head}
        <p className="aempty">Nothing on this for your question.</p>
        {pager}
      </section>
    )
  }
  return (
    <section className="asec" id={slot.id} aria-labelledby={`${slot.id}-h`}>
      {head}
      {first ? <p className="afirst">Landed first. Read it while the rest is weighed.</p> : null}
      {takeaway ? <p className="alead">{takeaway}</p> : null}
      <Body turn={turn} section={slot.section} sectionKey={slot.item!.id} sources={sources} />
      {offers ? <SlotOffers turn={turn} slot={slot} offers={offers} /> : null}
      {pager}
    </section>
  )
}

function SlotOffers({ turn, slot, offers }: { turn: string; slot: Slot; offers: Offers }) {
  const blocks = slot.section?.blocks ?? []
  if (slot.tag === "COURTS") {
    const shown = new Set(blocks.flatMap((b) => (b.type === "decisions" ? (b.items ?? []).map((i) => i.source_id) : [])))
    return (
      <>
        <AllDecisions turn={turn} found={offers.found} shown={shown} />
        {offers.department ? <DepartmentCard id={`dept-${turn}`} /> : null}
      </>
    )
  }
  if (slot.tag === "ACTION") {
    const due = blocks.find((b): b is Extract<Block, { type: "deadline" }> => b.type === "deadline")
    return due ? <RemindMe date={due.date} title={offers.title} /> : null
  }
  return null
}

function Pending({ id, head }: { id: string; head: React.ReactNode }) {
  return (
    <section className="asec pend" id={id} aria-busy="true">
      {head}
      <span className="ghost" style={{ width: "86%" }} />
      <span className="ghost" style={{ width: "68%" }} />
    </section>
  )
}

/** A section's blocks; quotes past the second fold into "+N more sources". */
function Body({ turn, section, sectionKey, sources }: { turn: string; section: Section; sectionKey: string; sources: Record<string, Source> }) {
  const openSource = useAnswerSession((s) => s.openSource)
  const shown: { block: Block; path: string }[] = []
  const extra: { source_id: string; para: number }[] = []
  let quotes = 0
  section.blocks.forEach((b, i) => {
    if (b.type === "quote" && ++quotes > MAX_QUOTES) extra.push({ source_id: b.source_id, para: b.para })
    else shown.push({ block: b, path: String(i) })
  })
  return (
    <BlockScopeContext.Provider value={{ turn, section: sectionKey, sources, citeNo: citeNumbers(section.blocks) }}>
      <div className="cbd">
        {shown.map(({ block, path }) => <BlockView key={path} block={block} path={path} />)}
        {extra.length ? (
          <Fold label={`+${extra.length} more ${extra.length === 1 ? "source" : "sources"}`}>
            <ul className="more">
              {extra.map((q, i) => (
                <li key={i}>
                  <button type="button" onClick={(e) => openSource({ turn, id: q.source_id, from: e.currentTarget })}>
                    {sources[q.source_id]?.name || sources[q.source_id]?.label || "Source"}
                  </button>
                  {q.para ? <span className="m">para {q.para}</span> : null}
                </li>
              ))}
            </ul>
          </Fold>
        ) : null}
      </div>
    </BlockScopeContext.Provider>
  )
}

/** Answers from before the fixed anatomy: their own planned headings, in plan order. */
function LegacySections({ turn, plan, sections, takeaways, sources, running }: AnswerSectionsProps) {
  return (
    <div className="asecs">
      {plan.map((item, i) => {
        const section = sections[item.id]
        const id = sectionId(turn, i)
        if (!section) {
          if (!running) return null
          return <Pending key={item.id} id={id} head={<h3>{idx(i)} · {item.heading}</h3>} />
        }
        return (
          <section className="asec" id={id} key={item.id} aria-labelledby={`${id}-h`}>
            <h3 id={`${id}-h`}>{idx(i)} · {item.heading}</h3>
            {takeaways[i] ? <p className="alead">{takeaways[i]}</p> : null}
            <Body turn={turn} section={section} sectionKey={item.id} sources={sources} />
          </section>
        )
      })}
    </div>
  )
}
